/**
 * @author       julian@zumper.com (Julian Napolitan)
 * @copyright    Copyright (c) 2019, Zumper
 * @description  fetchPredictionResults thunk
 */

import type { AutocompleteApiResult } from '@zcode/bizos'
import { BetaFeature, isBetaFeatureEnabled } from '@zcode/nr-utils'
import type { Dispatch, SetStateAction } from 'react'
import { logger } from '../../../../../logger'
import * as Api from '../../../../../shared/api'
import {
  clearFetchingKey,
  setFetchingKey,
  setIsFetching,
} from '../../../../../shared/modules/autocomplete/actions'
import { selectFetchingKey } from '../../../../../shared/modules/autocomplete/selectors'
import { getRouteProps } from '../../../../../shared/packages/history-middleware'
import type { Dispatch as NRDispatch } from '../../../../../shared/types'
import { useAutocompleteContext } from '../../AutocompleteContext'

const fetchPredictionResults =
  (
    doNotSendLatLng: boolean,
    exclude: Parameters<(typeof Api)['autocompleteResults']['query']>[0]['exclude'],
    searchTerm: string,
    lat: number | null,
    lng: number | null,
    setPredictionResults: Dispatch<SetStateAction<readonly AutocompleteApiResult[] | null>>
  ) =>
  (dispatch: NRDispatch) => {
    const { location } = dispatch(getRouteProps())
    const debug = isBetaFeatureEnabled(location, BetaFeature.AUTOCOMPLETE_ALL_RESULTS)
    const query = {
      debug: debug || undefined,
      lat: doNotSendLatLng ? undefined : (lat ?? undefined),
      lng: doNotSendLatLng ? undefined : (lng ?? undefined),
      query: `${searchTerm}`,
      ...{
        ...exclude,
      },
    }
    const key = JSON.stringify(query)

    // Need to get state from global redux for fetchingKey
    const fetchingKey = dispatch((_, getState) => selectFetchingKey(getState()))
    if (fetchingKey !== key) {
      dispatch(setFetchingKey(key))
      dispatch(setIsFetching(true))
      const results = Api.autocompleteResults.query(query)
      results
        .then(({ matches }) => {
          const fetchingKey = dispatch((_, getState) => selectFetchingKey(getState()))
          if (key === fetchingKey && Array.isArray(matches) && matches.length) {
            const predictionResults = matches
            setPredictionResults(predictionResults)
            dispatch(clearFetchingKey())
          } else if (key === fetchingKey) {
            setPredictionResults(null)
          }
          dispatch(setIsFetching(false))
        })
        .catch((err) => {
          dispatch(setIsFetching(false))
          logger.debug('Error fetching autocomplete results', err)
        })
    }
  }

export default fetchPredictionResults
